import React, { useState, useEffect, useCallback } from "react";

// Phase 6A.2.b — Refresh Queue (read-only + dismiss only)
// Surfaces cannibalization_refresh_candidate metrics emitted by the
// scheduler when a candidate keyword matches a published post on
// (service, intent, location) that's >=90 days old. The scheduler
// still allows the new keyword to schedule (advisory) — these cards
// just give the operator the option to refresh the OLD post instead
// of letting both compete.
//
// No "refresh in place" action this phase — that needs a dedicated
// published-post refresh endpoint with safety checks (separate
// phase). For now: View Existing Post (opens WP URL) + Dismiss.
export default function RefreshQueueTab({ clientId, api, authFetch }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${api}/api/clients/${clientId}/refresh-queue`);
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = await res.json();
      setCandidates(data.candidates || []);
    } catch (e) {
      setError(e?.message || String(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, api, authFetch]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (candidate) => {
    const key = `${candidate.prior_post.id}|${candidate.new_keyword}`;
    setBusyKey(key);
    try {
      const res = await authFetch(`${api}/api/clients/${clientId}/refresh-queue/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prior_post_id: candidate.prior_post.id,
          new_keyword: candidate.new_keyword,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { alert(`Dismiss failed: ${data.error || res.status}`); return; }
      // Drop the dismissed card from local state
      setCandidates(prev => prev.filter(c => `${c.prior_post.id}|${c.new_keyword}` !== key));
    } catch (e) {
      alert(`Dismiss threw: ${e?.message || e}`);
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return <div style={styles.empty}>Loading refresh queue…</div>;
  }

  if (error) {
    return <div style={styles.errorBanner}>Failed to load refresh queue: {error}</div>;
  }

  if (candidates.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}>✓</div>
        <div style={styles.emptyTitle}>No refresh candidates</div>
        <div style={styles.emptySub}>
          When the scheduler picks a keyword that matches an old (≥90 days) published post on the same service + intent + location, that post shows up here as a candidate to refresh in place.
        </div>
        <div style={styles.emptyHint}>The scheduler is parked behind the approval gate from Phase 6A.2.a — once you start approving queue rows, refresh candidates will accumulate here as scheduler runs identify them.</div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 12 }}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>Cannibalization advisory</div>
          <div style={styles.title}>{candidates.length} post{candidates.length === 1 ? "" : "s"} may be due for refresh</div>
        </div>
        <button onClick={load} style={styles.refreshBtn}>↻ Refresh list</button>
      </div>

      <div style={styles.intro}>
        Each card pairs a <strong>published post</strong> with a <strong>candidate keyword</strong> the scheduler is about to schedule (or already has). The two target the same service + intent + location. If the existing post is still relevant, consider refreshing it instead of letting both compete in search.
      </div>

      <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
        {candidates.map(c => {
          const key = `${c.prior_post.id}|${c.new_keyword}`;
          const isBusy = busyKey === key;
          return (
            <div key={key} style={styles.card}>
              {/* Prior post */}
              <div style={styles.section}>
                <div style={styles.sectionLabel}>Existing published post · {c.prior_post.age_days}d old</div>
                <div style={styles.priorTitle}>{c.prior_post.title || c.prior_post.slug || "(no title)"}</div>
                <div style={styles.priorMeta}>
                  {c.prior_post.taxonomy_service && <span style={styles.taxBadge}>{c.prior_post.taxonomy_service}</span>}
                  {c.prior_post.taxonomy_location && <span style={styles.taxBadge}>{c.prior_post.taxonomy_location}</span>}
                  {c.prior_post.taxonomy_intent && <span style={styles.taxBadge}>{c.prior_post.taxonomy_intent}</span>}
                  {c.prior_post.published_at && (
                    <span style={styles.dateMeta}>published {new Date(c.prior_post.published_at).toLocaleDateString()}</span>
                  )}
                </div>
                {c.prior_post.external_url && (
                  <a href={c.prior_post.external_url} target="_blank" rel="noopener noreferrer" style={styles.viewLink}>
                    View existing post ↗
                  </a>
                )}
              </div>

              <div style={styles.divider}>vs</div>

              {/* New candidate */}
              <div style={styles.section}>
                <div style={styles.sectionLabel}>New candidate keyword</div>
                <div style={styles.candKeyword}>{c.new_keyword}</div>
                <div style={styles.candDetail}>
                  Targets the same service + intent + location. If the existing post is stale, refresh it (via Archived Posts → edit) before the new one publishes — otherwise both will compete in search.
                </div>
              </div>

              {/* Actions */}
              <div style={styles.actions}>
                <button onClick={() => dismiss(c)} disabled={isBusy} style={styles.dismissBtn}>
                  {isBusy ? "Dismissing…" : "Dismiss"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  empty: { padding: "40px 20px", textAlign: "center", color: "#777" },
  emptyTitle: { fontSize: 14, color: "#aaa", marginBottom: 8, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.04em" },
  emptySub: { fontSize: 12, color: "#666", maxWidth: 580, margin: "0 auto", lineHeight: 1.6, fontFamily: "'Barlow', sans-serif" },
  emptyHint: { fontSize: 11, color: "#444", maxWidth: 580, margin: "16px auto 0", lineHeight: 1.5, fontStyle: "italic" },
  errorBanner: { padding: 12, color: "#fca5a5", background: "rgba(214,0,0,0.08)", border: "1px solid rgba(214,0,0,0.3)", borderRadius: 4, marginTop: 12, fontSize: 12 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 },
  eyebrow: { fontSize: 10, color: "#d60000", letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" },
  title: { fontSize: 16, fontWeight: 600, color: "#fff", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em", marginTop: 2 },
  refreshBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#999", padding: "6px 14px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
  intro: { fontSize: 12, color: "#888", lineHeight: 1.5, marginTop: 8, fontFamily: "'Barlow', sans-serif", maxWidth: 800 },
  card: { background: "#0d0d0d", border: "1px solid #1a1a1a", borderLeft: "3px solid #eab308", borderRadius: 4, padding: "14px 18px", display: "grid", gridTemplateColumns: "1fr auto 1fr auto", gap: 18, alignItems: "center" },
  section: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  sectionLabel: { fontSize: 10, color: "#666", letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 },
  priorTitle: { fontSize: 13, color: "#fff", fontWeight: 600, lineHeight: 1.3, fontFamily: "'Barlow', sans-serif", overflow: "hidden", textOverflow: "ellipsis" },
  priorMeta: { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  taxBadge: { fontSize: 9, color: "#fca5a5", padding: "2px 7px", background: "rgba(214,0,0,0.1)", border: "1px solid rgba(214,0,0,0.3)", borderRadius: 999, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.04em" },
  dateMeta: { fontSize: 10, color: "#666", fontFamily: "'Barlow', sans-serif" },
  viewLink: { fontSize: 11, color: "#d60000", textDecoration: "none", fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 4 },
  divider: { color: "#444", fontSize: 11, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.14em", textTransform: "uppercase", padding: "0 8px" },
  candKeyword: { fontSize: 14, color: "#fff", fontWeight: 600, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em" },
  candDetail: { fontSize: 11, color: "#888", lineHeight: 1.5, fontFamily: "'Barlow', sans-serif" },
  actions: { display: "flex", flexDirection: "column", gap: 6 },
  dismissBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#aaa", padding: "6px 14px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", whiteSpace: "nowrap" },
};
